import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// URL state for the admin portal:
//   /admin?tab=certs&q=98220183
// Plus arbitrary filter keys per tab (e.g. sellerEmail, supertype).
//
// goTo({ tab, ...filters }) swaps tabs and pre-applies filters, so any RelLink
// can jump entities while staying on the same /admin page.

const DEFAULT_TAB = 'listings';

export default function useAdminTab() {
    const navigate = useNavigate();
    const location = useLocation();

    const params = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );

    const tab = params.get('tab') || DEFAULT_TAB;

    // Filters are everything except `tab`. Tabs read whatever filter keys they care about.
    const filters = useMemo(() => {
        const obj = {};
        for (const [k, v] of params.entries()) {
            if (k !== 'tab') obj[k] = v;
        }
        return obj;
    }, [params]);

    const goTo = useCallback(
        ({ tab: nextTab, ...nextFilters } = {}, opts = {}) => {
            const sp = new URLSearchParams();
            sp.set('tab', nextTab || tab);
            for (const [k, v] of Object.entries(nextFilters)) {
                if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
            }
            const search = `?${sp.toString()}`;
            if (opts.replace) navigate({ pathname: '/admin', search }, { replace: true });
            else navigate({ pathname: '/admin', search });
        },
        [navigate, tab]
    );

    // One-shot bookmark for legacy state passed via navigate('/admin', { state: { section, certNumber } })
    const [legacyApplied, setLegacyApplied] = useState(false);
    useEffect(() => {
        if (legacyApplied) return;
        const ls = location.state;
        if (ls && ls.section) {
            const mapped = ls.section.toLowerCase();
            goTo({ tab: mapped, ...(ls.certNumber ? { certNumber: ls.certNumber } : {}) }, { replace: true });
            setLegacyApplied(true);
        }
    }, [location.state, legacyApplied, goTo]);

    return { tab, filters, goTo };
}
